import cn from 'clsx'
import React, { FC, useRef, useEffect, useState } from 'react'
import s from './FullHeightSection.module.scss'
import { CustomImage, Fade, VideoPlayer } from '@components/ui'
import { CustomImageProps } from '@components/ui/CustomImage/CustomImage'
import {
  useIntersectionObserver,
  useWindowSize,
  useEventListener,
} from 'usehooks-ts'
import { useUI } from '@components/ui/context'
import { useHomepageContext } from '@context/HomepageContext'
import { isMobileWidth, isDesktopWidth } from '@utils/helpers'
import { gsap } from 'gsap/dist/gsap'
import { ScrollToPlugin } from 'gsap/dist/ScrollToPlugin'
import { RequestFile } from '@generated/model/models'

if (typeof window !== 'undefined') {
  // Due to SSR we can only register when we are in the browser
  gsap.registerPlugin(ScrollToPlugin)
}

interface FullHeightSectionProps {
  children?: any
  className?: string
  imageClassName?: string
  image?: CustomImageProps
  imageProps?: any
  source?: RequestFile | null
  sourceUrl?: string | null
  el?: HTMLElement
  slideIndex: number
  id: string
}

const FullHeightSection: FC<FullHeightSectionProps> = ({
  children,
  className,
  imageClassName,
  image,
  imageProps,
  source,
  sourceUrl,
  slideIndex,
  el = 'div',
  id,
}) => {
  const {
    activeSection,
    setActiveSection,
    activeSectionHeight,
    setActiveSectionHeight,
    activeSlide,
    setActiveSlide,
    maxHeight,
    setMaxHeight,
  } = useHomepageContext()

  const [localHeight, setLocalHeight] = useState(0)

  const { width, height } = useWindowSize()
  const isMobile = isMobileWidth(width)
  const isHidden =
    activeSlide &&
    activeSlide > 0 &&
    activeSlide != slideIndex &&
    activeSection != 0 &&
    isDesktopWidth(width)

  const rootClassName = isHidden
    ? s.hidden
    : cn(s.root, className, {
        [s.modalActive]: activeSection != 0 && activeSection == activeSlide,
      })

  const mergedImageClassName = cn(
    s.container,
    imageClassName ? imageClassName : ''
  )

  const ref = useRef<HTMLDivElement | null>(null)

  const entry = useIntersectionObserver(ref, {
    threshold: isMobile ? 0.5 : 0.66,
  })

  const isVisible = !!entry?.isIntersecting

  if (isVisible) {
    setActiveSlide(slideIndex)
  }

  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (ref.current) {
      const maxEl = [...ref.current.children].reduce((cur, acc) =>
        cur.getBoundingClientRect().height > acc.getBoundingClientRect().height
          ? cur
          : acc
      )
      setMaxHeight((maxHeight: number) =>
        maxEl.getBoundingClientRect().height > maxHeight
          ? maxEl.getBoundingClientRect().height
          : maxHeight
      )
      if (ref.current.children) {
        setLocalHeight(
          [...ref.current.children][0].getBoundingClientRect().height
        )
      }
    }
  }, [height, setMaxHeight])

  let Component: React.ComponentType<React.HTMLAttributes<HTMLDivElement>> =
    el as any

  return (
    <div
      ref={ref}
      className={rootClassName}
      id={id}
      style={isMobile && isMounted ? {} : { minHeight: localHeight + 'px' }}
    >
      {children}
      {image && (
        <div className={mergedImageClassName}>
          {source || sourceUrl ? (
            <div className={s.videoContainer}>
              {isMounted && (
                <VideoPlayer
                  className={s.videoInner}
                  data={{
                    ...(image as any),
                    video: source,
                    video_url: sourceUrl,
                  }}
                />
              )}
            </div>
          ) : (
            <>
              <CustomImage
                data={image}
                layout="fill"
                objectFit="cover"
                {...imageProps}
              />
              <Fade className={s.fade} />
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default FullHeightSection
